import { useMemo } from "react";

const MIN_GROUP_SIZE = 2;
const MAX_INTERVAL_MS = 60000; // 60 segundos

const IMAGE_MEDIA_TYPES = new Set(["image", "imagemessage"]);
const PLACEHOLDER_BODIES = new Set([
  "imagem",
  "image",
  "[imagem]",
  "[image]",
  "sticker",
  "áudio",
  "audio",
  "vídeo",
  "video",
  "documento",
  "document",
  "mídia",
  "media",
  "📎 mídia",
  "📎 mídia",
]);

const isImageMediaType = (mediaType) => {
  const mt = String(mediaType || "").toLowerCase();
  return IMAGE_MEDIA_TYPES.has(mt) || mt.includes("image");
};

const mediaUrlLooksLikeImage = (url) =>
  !!url && /\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i.test(String(url));

const COMMON_COUNTRY_CODES = ["55", "1", "44", "33", "49", "39", "34"];

/** LID Meow/Meta: 13–15 dígitos sem código de país comum (não confundir com telefone BR). */
function isLikelyLid(value) {
  if (!value) return false;
  const s = String(value).toLowerCase();
  if (s.includes("@lid") || s.startsWith("lid_")) return true;
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 13 && digits.length <= 15) {
    return !COMMON_COUNTRY_CODES.some((code) => digits.startsWith(code));
  }
  return false;
}

function resolveStableSenderPhone(msg) {
  try {
    const raw =
      typeof msg?.dataJson === "string" ? JSON.parse(msg.dataJson) : msg?.dataJson;
    const alt = raw?.senderAlt || raw?.SenderAlt;
    if (alt && String(alt).includes("@s.whatsapp.net")) {
      const d = phoneDigitsFromJid(alt);
      if (d && !isLikelyLid(d)) return d;
    }
  } catch {
    /* ignore */
  }

  const part = extractParticipantJid(msg);
  if (part && !String(part).toLowerCase().includes("@lid")) {
    const d = phoneDigitsFromJid(part);
    if (d && !isLikelyLid(d)) return d;
  }

  const contactDigits = String(msg.contact?.number || "").replace(/\D/g, "");
  if (contactDigits.length >= 10 && contactDigits.length <= 13 && !isLikelyLid(contactDigits)) {
    return contactDigits;
  }

  return null;
}

/** JID do participante (grupos Meow/Baileys) — estável mesmo com contatos LID/PN duplicados. */
function extractParticipantJid(msg) {
  try {
    const raw =
      typeof msg?.dataJson === "string" ? JSON.parse(msg.dataJson) : msg?.dataJson;
    const alt = raw?.senderAlt || raw?.SenderAlt;
    if (alt && String(alt).includes("@s.whatsapp.net")) {
      return String(alt).trim();
    }
  } catch {
    /* ignore */
  }
  if (msg?.participant) {
    const p = String(msg.participant).trim();
    const local = p.split("@")[0] || "";
    if (!p.toLowerCase().endsWith("@lid") && !isLikelyLid(local)) return p;
  }
  try {
    const raw =
      typeof msg?.dataJson === "string" ? JSON.parse(msg.dataJson) : msg?.dataJson;
    const p = raw?.key?.participant || raw?.participant;
    return p ? String(p).trim() : null;
  } catch {
    return null;
  }
}

function phoneDigitsFromJid(jid) {
  if (!jid) return "";
  const local = String(jid).split("@")[0] || "";
  const digits = local.replace(/\D/g, "");
  return digits.length >= 10 ? digits : "";
}

function normalizeGroupSenderKey(participantJid) {
  if (!participantJid) return null;
  const s = participantJid.toLowerCase();
  const local = s.split("@")[0] || "";
  if (s.endsWith("@lid")) return `lid:${local.replace(/\D/g, "") || local}`;
  const digits = phoneDigitsFromJid(s);
  if (digits) return `pn:${digits}`;
  return `jid:${s}`;
}

function messageIsInGroup(msg, ticketIsGroup) {
  if (ticketIsGroup) return true;
  const jid = String(msg?.remoteJid || "").toLowerCase();
  return jid.endsWith("@g.us");
}

/**
 * Hook que agrupa mensagens de imagem consecutivas do mesmo remetente.
 */
const useImageGrouping = (messagesList, enabled = true, isGroup = false) => {
  return useMemo(() => {
    const groupMap = new Map();

    if (!enabled || !messagesList || messagesList.length === 0) {
      return groupMap;
    }

    const groups = [];
    let currentGroup = [];

    const getBasename = (url) => {
      try {
        return decodeURIComponent(url.split("/").pop().split("?")[0]).trim();
      } catch {
        return url.split("/").pop().split("?")[0].trim();
      }
    };

    const isLikelyAutoBody = (body, mediaUrl) => {
      const b = (body || "").trim();
      if (!b) return true;
      const lower = b.toLowerCase();
      if (PLACEHOLDER_BODIES.has(lower)) return true;
      if (/^[A-Fa-f0-9\-_]{16,}(\.[a-z0-9]+)?$/i.test(b)) return true;
      const base = getBasename(mediaUrl || "");
      if (base && lower === base.toLowerCase()) return true;
      if (/\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i.test(b) && b.length < 200) return true;
      if (base && base.toLowerCase().endsWith(lower)) return true;
      return false;
    };

    const hasCaption = (msg) => {
      const body = (msg.body || "").trim();
      if (!body) return false;
      if (isLikelyAutoBody(body, msg.mediaUrl)) return false;
      return true;
    };

    const isImageMessage = (msg) => {
      if (!msg || msg.isDeleted || !msg.mediaUrl) return false;

      const mt = String(msg.mediaType || "").toLowerCase();
      const urlIsImage = mediaUrlLooksLikeImage(msg.mediaUrl);
      const isImage =
        isImageMediaType(msg.mediaType) ||
        (mt === "media" && urlIsImage) ||
        urlIsImage;

      if (!isImage) return false;
      if (hasCaption(msg)) return false;
      return true;
    };

    /** Stubs/reações vazios entre imagens não quebram o álbum (Meow). */
    const isIgnorableBetweenImages = (msg) => {
      if (!msg || msg.isDeleted) return true;
      const mt = String(msg.mediaType || "").toLowerCase();
      if (mt === "reactionmessage" || mt === "reaction") return true;
      if (!msg.mediaUrl) {
        const body = (msg.body || "").trim();
        if (!body) return true;
        if (PLACEHOLDER_BODIES.has(body.toLowerCase())) return true;
        if (/^[A-Fa-f0-9\-_]{8,}$/.test(body)) return true;
      }
      return false;
    };

    const getSender = (msg) => {
      if (msg.fromMe) return "__me__";

      const inGroupChat = messageIsInGroup(msg, isGroup);

      // Ticket individual: contactId é o mais estável
      if (!inGroupChat) {
        if (msg.contactId != null) return `cid:${msg.contactId}`;
        if (msg.contact?.id != null) return `cid:${msg.contact.id}`;
      }

      const stablePhone = resolveStableSenderPhone(msg);
      if (stablePhone) return `pn:${stablePhone}`;

      if (msg.contactId != null) return `cid:${msg.contactId}`;
      if (msg.contact?.id != null) return `cid:${msg.contact.id}`;

      const part = extractParticipantJid(msg);
      if (inGroupChat && part) {
        const partKey = normalizeGroupSenderKey(part);
        if (partKey) return partKey;
      }

      return "__unknown__";
    };

    const getTimestamp = (msg) => new Date(msg.createdAt).getTime();

    for (let i = 0; i < messagesList.length; i++) {
      const msg = messagesList[i];

      if (isIgnorableBetweenImages(msg)) continue;

      if (isImageMessage(msg)) {
        if (currentGroup.length === 0) {
          currentGroup.push(msg);
        } else {
          const lastMsg = currentGroup[currentGroup.length - 1];
          const sameSender = getSender(msg) === getSender(lastMsg);
          const withinInterval =
            Math.abs(getTimestamp(msg) - getTimestamp(lastMsg)) <= MAX_INTERVAL_MS;

          if (sameSender && withinInterval) {
            currentGroup.push(msg);
          } else {
            if (currentGroup.length >= MIN_GROUP_SIZE) {
              groups.push([...currentGroup]);
            }
            currentGroup = [msg];
          }
        }
      } else {
        if (currentGroup.length >= MIN_GROUP_SIZE) {
          groups.push([...currentGroup]);
        }
        currentGroup = [];
      }
    }

    if (currentGroup.length >= MIN_GROUP_SIZE) {
      groups.push([...currentGroup]);
    }

    for (const group of groups) {
      const groupId = `img-group-${group[0].id}`;
      for (let i = 0; i < group.length; i++) {
        groupMap.set(String(group[i].id), {
          groupId,
          messages: group,
          isFirst: i === 0,
          isGrouped: true,
        });
      }
    }

    return groupMap;
  }, [messagesList, enabled, isGroup]);
};

export default useImageGrouping;
